import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useWatch } from "react-hook-form";

import {
  shipmentSchema,
  shipmentSchemaInternational,
  shipmentSchemaDomestic,
  shipmentSchemaSea,
  shipmentSchemaAir,
} from "../../schemavalidations/shipmentschema";
import {
  CreateShipmentData,
  UpdateShipmentData,
  Shipment,
  PartyData,
  ShipmentType,
  TransportMode,
  SeaDetails,
  AirDetails,
} from "../../types";
import { getUsers } from "../../store/slices/userSlice";
import { getVehicles } from "../../store/slices/vehiclesSlice";
import { useAppSelector, useAppDispatch } from "../../hooks/useStore";

// imports - all the sub-components
import { GeneralInformation } from "./GeneralInformation";
import { PickupDeliverySection } from "./PickupDeliverySection";
import { CarrierClientSection } from "./CarrierClientSection";
import { TransportSection } from "./TransportSection";
import { SeaDetailsSection } from "./SeaDetailsSection";
import { BillOfLadingSection } from "./BillOfLadingSection";
import { AirDetailsSection } from "./AirDetailsSection";
import { AwbSection } from "./AwbSection";
import { ForwarderSection } from "./ForwarderSection";
import { BLRemarksSection } from "./BLRemarksSection";
import { CargoItemsSection } from "./CargoSection";
import { PricingSection } from "./PricingSection";
import { AttachmentsSection } from "./AttachmentsSection";
import { WaybillSection } from "./WayBillSection";
import { FormActions } from "./FormAction";
import { CommentSection } from "../comment/CommentSection";
import ReclamationSection from "../shipments/ReclamationSection";
import ShipmentReadonly from "../shipments/ShipmentReadonly";
import ConfirmationDialog from "../modals/ConfirmationDialog";
import { decideStatusChange } from "../../utils/statusTransition";

// Constants and types
import { STATUS_STEPS, CURRENCIES } from "../../constants/formOptions";
import {
  downloadShipmentAttachment,
  createWayBill,
  toggleAttachmentPublic,
  getShipment,
} from "../../store/slices/shipmentSlice";
import { getServices } from "../../store/slices/servicesSlice";
import { getCostServices } from "../../store/slices/costServicesSlice";
import { shipmentAPI } from "../../services/api";

// import cmr preview
import { CmrPreview } from "../shipments/CMRPreview";
import { BolPreview } from "../shipments/BOLPreview";
import { AwbPreview } from "../shipments/AWBPreview";
import DeliveryNotePreview from "../shipments/DeliveryNotePreview";
import { buildCmrOrder } from "../../utils/cmrPreviewDataGenerator";
import { FileText, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { ServicesSection } from "./ServicesSection";
import { CostServicesSection } from "./CostServicesSection";
import InvoiceRefs from "../invoices/InvoiceRefs";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { validateEftiFields } from "../../utils/eftiValidation";

import { Select, FormField, Input } from "../UI/FormField";

const GOOGLE_PLACES_API_KEY =
  import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "";

interface ShipmentFormProps {
  shipment?: Shipment;
  /**
   * True when `shipment` is a "Copy" prefill snapshot rather than a real,
   * persisted shipment being edited. Reuses the edit-mode field mapping to
   * populate the form, but the form still submits as a new create (status
   * and shipmentDate are reset to their fresh-shipment defaults).
   */
  isCopySource?: boolean;
  /**
   * Set when creating a shipment from a synced SO/PO order ("Create
   * Shipment" on the Orders page). Only used for the info banner — the
   * actual linking happens in ShipmentPage's create handler.
   */
  sourceOrder?: {
    id: string;
    number: string;
    type?: string;
    rawIncoterm?: string | null;
  };
  onSubmit: (data: CreateShipmentData | UpdateShipmentData) => Promise<boolean>;
  loading: boolean;
  isEditMode?: boolean;
  shipmentNumber?: string;
  onAssign: () => void;
  onUnassign: () => void;
  isAssigned: boolean;
  initialType?: ShipmentType | null;
  initialTransportMode?: TransportMode | null;
  handleGoBack: () => void;
}

export const ShipmentForm: React.FC<ShipmentFormProps> = ({
  shipment,
  isCopySource = false,
  sourceOrder,
  onSubmit,
  loading,
  isEditMode = false,
  shipmentNumber,
  onAssign,
  onUnassign,
  isAssigned,
  initialType,
  initialTransportMode,
  handleGoBack,
}) => {
  const { t } = useTranslation("shipments");
  const { t: tOrders } = useTranslation("orders");
  // Core state
  const [readonly, setReadonly] = useState(isEditMode);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [isWayBillCreated, setIsWayBillCreated] = useState(false);
  const [isAssignedLocal, setIsAssignedLocal] = useState(isAssigned);
  const [isCmrCollapsed, setIsCmrCollapsed] = useState(true);
  const [clientSyncMode, setClientSyncMode] = useState<
    "none" | "pickup" | "delivery"
  >("none");
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [vehiclesLoaded, setVehiclesLoaded] = useState(false);

  /*
   * DEAD STATE — kept commented for reference. Drove the legacy modal-based
   * Add/Edit-partner flow. Replaced by inline partner fields in the section
   * components (PickupDeliverySection / CarrierClientSection / TransportSection).
   *
   * const [snapshots, setSnapshots] = useState<{
   *   pickup?: EntitySnapshot;
   *   delivery?: EntitySnapshot;
   *   carrier?: EntitySnapshot;
   *   client?: EntitySnapshot;
   * }>({});
   * const [modalState, setModalState] = useState<EntityModalState>({
   *   isOpen: false,
   *   type: null,
   *   mode: null,
   *   snapshot: undefined,
   * });
   */

  // Redux state
  const { countries } = useAppSelector((state) => state.company);
  const { sendEmailLoading: WayBillLoading } = useAppSelector(
    (state) => state.shipment,
  );
  const { users } = useAppSelector((state) => state.users);
  const { vehicles } = useAppSelector((state) => state.vehicles);
  const trucks = useMemo(
    () => vehicles.filter((v) => v.type === "TRUCK"),
    [vehicles],
  );

  const trailers = useMemo(
    () => vehicles.filter((v) => v.type === "TRAILER"),
    [vehicles],
  );

  const { isLoaded, services } = useAppSelector((state) => state.services);
  const { costServices } = useAppSelector((state) => state.costServices);

  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useForm<CreateShipmentData>({
    resolver: yupResolver(shipmentSchema) as any,
    mode: "onChange",
    reValidateMode: "onChange",
    context: { isCreateMode: !isEditMode, shipmentType: shipment?.type },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "cargoItems",
  });

  const {
    fields: serviceFields,
    append: appendService,
    remove: removeService,
  } = useFieldArray({
    control,
    name: "services",
  });

  const {
    fields: costServiceFields,
    append: appendCostService,
    remove: removeCostService,
  } = useFieldArray({
    control,
    name: "costServices",
  });

  // Watch critical fields
  const pickupData = useWatch({ control, name: "pickup" });
  const deliveryData = useWatch({ control, name: "delivery" });
  const cargoItemsData = useWatch({ control, name: "cargoItems" }) || [];
  const additionalServicesData = useWatch({ control, name: "services" }) || [];
  const costServicesData = useWatch({ control, name: "costServices" }) || [];
  const pickup = useWatch({ control, name: "pickup" });
  const delivery = useWatch({ control, name: "delivery" });
  const carrier = useWatch({ control, name: "carrier" });
  const client = useWatch({ control, name: "client" });
  const cargoItems = useWatch({ control, name: "cargoItems" });
  const pricing = useWatch({ control, name: "pricing" });
  const additionalNotes = useWatch({ control, name: "additionalNotes" });
  const referenceNumber = useWatch({ control, name: "referenceNumber" });
  const type = useWatch({ control, name: "type" });
  const assignedDriver = useWatch({ control, name: "assignedDriver" });
  const seaDetailsData = useWatch({ control, name: "seaDetails" });
  const airDetailsData = useWatch({ control, name: "airDetails" });
  const transportMode =
    useWatch({ control, name: "transportMode" }) || "ROAD";

  const transportModeField = register("transportMode");
  const transportModeOptions = [
    { value: "ROAD", label: t("transportMode.road") },
    { value: "SEA", label: t("transportMode.sea") },
    { value: "AIR", label: t("transportMode.air") },
  ];

  const order = useMemo(
    () =>
      buildCmrOrder({
        shipmentNumber,
        pickup,
        delivery,
        carrier,
        client,
        referenceNumber,
        cargoItems,
        pricing,
        additionalNotes,
        type,
        assignedDriver,
        seaDetails: seaDetailsData as any,
      }),
    [
      pickup,
      delivery,
      carrier,
      cargoItems,
      pricing,
      client,
      referenceNumber,
      additionalNotes,
      type,
      assignedDriver,
      seaDetailsData,
    ],
  );

  // Label for the right-side document preview pane — BOL for sea, otherwise
  // CMR (International) / Delivery Note (Domestic).
  const previewDocLabel =
    transportMode === "SEA"
      ? t("form.bol")
      : transportMode === "AIR"
        ? t("form.awb")
        : order.type === "Domestic"
          ? t("form.deliveryNote")
          : t("form.cmr");

  const countryOptions = useMemo(
    () =>
      (countries ?? []).flat().map((country) => ({
        value: country._id,
        label: country.name,
      })),
    [countries],
  );

  const totalQuantity = useMemo(() => {
    return cargoItemsData.reduce((sum, item) => {
      const q = Number(item.quantity);
      return Number.isFinite(q) ? sum + q : sum;
    }, 0);
  }, [cargoItemsData]);

  const { totalForEachRow, grandTotal } = useMemo(() => {
    if (!additionalServicesData || additionalServicesData.length === 0) {
      return { totalForEachRow: [], grandTotal: 0 };
    }

    const totalForEachRow = additionalServicesData.map((item) => ({
      ...item,
      total: (item.qty || 0) * (item.price || 0),
    }));

    const grandTotal = totalForEachRow.reduce(
      (sum, item) => sum + item.total,
      0,
    );

    return { totalForEachRow, grandTotal };
  }, [additionalServicesData]);

  const {
    totalForEachRow: costTotalForEachRow,
    grandTotal: costGrandTotal,
  } = useMemo(() => {
    if (!costServicesData || costServicesData.length === 0) {
      return { totalForEachRow: [], grandTotal: 0 };
    }

    const totalForEachRow = costServicesData.map((item) => ({
      ...item,
      total: (item.qty || 0) * (item.price || 0),
    }));

    const grandTotal = totalForEachRow.reduce(
      (sum, item) => sum + item.total,
      0,
    );

    return { totalForEachRow, grandTotal };
  }, [costServicesData]);

  const totalWeight = useMemo(() => {
    return cargoItemsData.reduce((sum, item) => {
      const w = Number(item.weightKg);
      return Number.isFinite(w) ? sum + Number(w.toFixed(2)) : sum;
    }, 0);
  }, [cargoItemsData]);

  // Sum the per-row stored ldm / volumeM3 values. The row watcher in
  // CargoSection auto-fills these from dimensions when empty, but the user
  // can also type their own values — the totals must reflect whatever's
  // actually on the row, not a re-computation from L/W/H that would ignore
  // manual entries.
  const totalLDM = useMemo(() => {
    return cargoItemsData.reduce((sum, item) => {
      const ldm = Number(item.ldm);
      return Number.isFinite(ldm) && ldm > 0 ? sum + ldm : sum;
    }, 0);
  }, [cargoItemsData]);

  const totalVolume = useMemo(() => {
    return cargoItemsData.reduce((sum, item) => {
      const v = Number(item.volumeM3);
      return Number.isFinite(v) && v > 0 ? sum + v : sum;
    }, 0);
  }, [cargoItemsData]);

  // Memoized utility functions
  const getCountryId = useCallback(
    (country: string | { _id: string } | null | undefined): string => {
      if (!country) return "";
      if (typeof country === "object" && "_id" in country) return country._id;
      return country;
    },
    [],
  );

  const getErrorMessage = useCallback((error: any): string | undefined => {
    if (error && typeof error === "object" && "message" in error) {
      return error.message;
    }
    return undefined;
  }, []);

  // Clear fields that don't belong to the newly selected transport mode, so
  // stale data is never submitted. Mirrors the backend cleanup in
  // updateShipment. Driven by the select's onChange (not a watcher) so it only
  // fires on real user interaction — never when an existing shipment loads.
  const handleTransportModeChange = useCallback(
    (newMode: string) => {
      if (newMode !== "SEA") setValue("seaDetails", undefined as any);
      if (newMode !== "AIR") setValue("airDetails", undefined as any);

      if (newMode !== "ROAD") {
        setValue("assignedDriver", null);
        setValue("carrier.truckLicensePlate", null);
        setValue("carrier.trailerLicensePlate", null);
        setValue("pricing.mileage", undefined as any);
        setValue("pricing.pricePerKm", undefined as any);
        // Level 0 (attachment-based) needs the road driver app, so it isn't
        // available for SEA/AIR. Keep an existing Level 2, otherwise fall back
        // to Level 1.
        if (getValues("pohLevel") !== 2) setValue("pohLevel", 1);
      }

      const items = getValues("cargoItems") || [];
      items.forEach((_, i) => {
        if (newMode !== "ROAD")
          setValue(`cargoItems.${i}.ldm` as any, undefined);
        if (newMode !== "AIR")
          setValue(`cargoItems.${i}.chargeableWeight` as any, undefined);
      });
    },
    [setValue, getValues],
  );

  // Handlers
  const handleUnlockFields = useCallback(() => {
    statusOverrideRef.current = false;
    setReadonly(false);
  }, []);

  const handleLockFields = useCallback(() => {
    setReadonly(true);
  }, []);

  const onFormSubmit = async (data: CreateShipmentData) => {
    // Sanity check: delivery date must not be before pickup date.
    // Runs only when both are present; format-only rule, no longer
    // gated by the old documentRequire flag.
    const pickupDate = data.pickup?.date;
    const deliveryDate = data.delivery?.date;

    if (pickupDate && deliveryDate) {
      const pickupDateTime = data.pickup?.time
        ? new Date(`${pickupDate}T${data.pickup.time}`)
        : new Date(`${pickupDate}T00:00:00`);

      const deliveryDateTime = data.delivery?.time
        ? new Date(`${deliveryDate}T${data.delivery.time}`)
        : new Date(`${deliveryDate}T00:00:00`);

      if (deliveryDateTime < pickupDateTime) {
        toast.error(t("form.deliveryBeforePickupError"));
        return;
      }
    }

    // On edit, carry the one-shot statusOverride flag so the backend permits a
    // status advance that bypasses a POH signature / driver-file step.
    const payload = isEditMode
      ? ({ ...data, statusOverride: statusOverrideRef.current } as typeof data)
      : data;

    const success = await onSubmit(payload);

    if (isEditMode && success) {
      setReadonly(true);
      statusOverrideRef.current = false;
    }
  };

  // Validate the shipment document (CMR for International, Delivery Note for
  // Domestic) against the strict schema. Pure feedback: success toast on pass,
  // field-level errors on fail (red text under each input). The toast is a
  // short one-liner — the per-field errors do the heavy explaining.
  const handleValidateCmr = useCallback(async () => {
    const values = getValues();
    const mode = values?.transportMode ?? "ROAD";
    const isSea = mode === "SEA";
    const isAir = mode === "AIR";
    const isDomestic = values?.type === "Domestic";
    const schema = isSea
      ? shipmentSchemaSea
      : isAir
        ? shipmentSchemaAir
        : isDomestic
          ? shipmentSchemaDomestic
          : shipmentSchemaInternational;

    const passedKey = isSea
      ? "form.bolValidationPassed"
      : isAir
        ? "form.awbValidationPassed"
        : isDomestic
          ? "form.deliveryNoteValidationPassed"
          : "form.cmrValidationPassed";
    const failedKey = isSea
      ? "form.bolValidationFailed"
      : isAir
        ? "form.awbValidationFailed"
        : isDomestic
          ? "form.deliveryNoteValidationFailed"
          : "form.cmrValidationFailed";

    clearErrors();
    try {
      await schema.validate(values, {
        abortEarly: false,
        context: { requireRegistrationCode: true },
      });
      toast.success(t(passedKey));
    } catch (err: any) {
      if (err?.inner && Array.isArray(err.inner) && err.inner.length > 0) {
        err.inner.forEach((e: any) => {
          if (e.path) {
            // Yup produces bracket-notation paths for array items
            // (e.g. `cargoItems[0].type`). react-hook-form's setError
            // needs dot-notation (`cargoItems.0.type`) for the error to
            // land inside `errors.cargoItems[0]` so per-row inputs can
            // read it.
            const rhfPath = e.path.replace(/\[(\d+)\]/g, ".$1");
            setError(rhfPath as any, {
              type: "cmr",
              message: e.message,
            });
          }
        });
      }
      toast.error(t(failedKey));
    }
  }, [getValues, setError, clearErrors, t]);

  // Validate eFti — same UX pattern as Validate CMR: field-level errors
  // under each input + a short toast. Runs against live form values
  // (so the user doesn't have to save before validating).
  const handleValidateEfti = useCallback(() => {
    const values = getValues();
    const errors = validateEftiFields(values, t);

    clearErrors();
    if (errors.length === 0) {
      toast.success(t("form.eftiValidationPassed"));
      return;
    }

    errors.forEach((e) => {
      if (e.path) {
        const rhfPath = e.path.replace(/\[(\d+)\]/g, ".$1");
        setError(rhfPath as any, { type: "efti", message: e.message });
      }
    });
    toast.error(t("form.eftiValidationFailedShort"));
  }, [getValues, setError, clearErrors, t]);

  useEffect(() => {
    if (!usersLoaded) {
      const params = {
        search: "",
        page: 1,
        limit: 1000,
        status: "",
        role: "driver",
      };
      dispatch(getUsers(params));
      setUsersLoaded(true);
    }
  }, [dispatch, usersLoaded]);

  useEffect(() => {
    if (!vehiclesLoaded) {
      const params = {
        search: "",
        page: 1,
        limit: 1000,
      };
      dispatch(getVehicles(params));
      setVehiclesLoaded(true);
    }
  }, [dispatch, vehiclesLoaded]);

  useEffect(() => {
    if (!isLoaded) {
      dispatch(getServices({ search: "", page: 1, limit: 1000 }));
    }
  }, [isLoaded, dispatch]);

  useEffect(() => {
    dispatch(getCostServices({ search: "", page: 1, limit: 1000 }));
  }, [dispatch]);

  // Initialize form data
  useEffect(() => {
    if (shipment && (isEditMode || isCopySource)) {
      const type = (shipment.type as ShipmentType) || "International";
      reset({
        type: type,
        // A copy always starts a fresh shipment: status and shipmentDate
        // never carry over from the source, regardless of what it was.
        status: isCopySource ? "Order" : shipment.status,
        shipmentDate: isCopySource
          ? new Date().toISOString().split("T")[0]
          : shipment.shipmentDate.split("T")[0],
        referenceNumber: shipment.referenceNumber || "",
        pickup: {
          date: shipment?.pickup?.date?.split("T")[0] || undefined,
          time: shipment?.pickup?.time || "",
          companyName: shipment?.pickup?.companyName || "",
          registerationCode: shipment?.pickup?.registerationCode || "",
          phoneNumber: shipment?.pickup?.phoneNumber || "",
          emailAddress: shipment?.pickup?.emailAddress || "",
          address: {
            ...shipment?.pickup?.address,
            country: getCountryId(shipment?.pickup?.address?.country || ""),
          },
        },
        delivery: {
          date: shipment?.delivery?.date?.split("T")[0] || undefined,
          time: shipment?.delivery?.time || "",
          companyName: shipment?.delivery?.companyName || "",
          registerationCode: shipment?.delivery?.registerationCode || "",
          phoneNumber: shipment?.delivery?.phoneNumber || "",
          emailAddress: shipment?.delivery?.emailAddress || "",
          address: {
            ...(shipment?.delivery?.address || {}),
            country: getCountryId(shipment?.delivery?.address?.country || ""),
          },
        },
        client: shipment.client && {
          // Preserve the client→Partner link so editing a shipment doesn't drop
          // it (the backend re-resolves/clears it based on what's sent).
          partnerId: shipment?.client?.partnerId ?? null,
          companyName: shipment?.client?.companyName || "",
          registerationCode: shipment?.client?.registerationCode || "",
          phoneNumber: shipment?.client?.phoneNumber || "",
          emailAddress: shipment?.client?.emailAddress || "",
          address: {
            ...shipment?.client?.address,
            country: getCountryId(shipment?.client?.address?.country || ""),
          },
        },
        clientSync: {
          clientSameAsPickup: shipment.clientSync?.clientSameAsPickup ?? false,
          clientSameAsDelivery:
            shipment.clientSync?.clientSameAsDelivery ?? false,
        },
        pohLevel: ((shipment.pohLevel ?? 0) as 0 | 1 | 2),
        incoterms: shipment.incoterms || undefined,
        place: shipment.place || undefined,
        direction: shipment.direction || undefined,
        loadType: shipment.loadType || undefined,
        transportMode: shipment.transportMode || "ROAD",
        seaDetails: shipment.seaDetails
          ? {
              shippingLine: shipment.seaDetails.shippingLine || "",
              vesselName: shipment.seaDetails.vesselName || "",
              imoNumber: shipment.seaDetails.imoNumber || "",
              voyageNumber: shipment.seaDetails.voyageNumber || "",
              bookingNumber: shipment.seaDetails.bookingNumber || "",
              portOfLoading: shipment.seaDetails.portOfLoading || "",
              portOfDischarge: shipment.seaDetails.portOfDischarge || "",
              placeOfReceipt: shipment.seaDetails.placeOfReceipt || "",
              placeOfDelivery: shipment.seaDetails.placeOfDelivery || "",
              notifyParty: {
                company: shipment.seaDetails.notifyParty?.company || "",
                contact: shipment.seaDetails.notifyParty?.contact || "",
                email: shipment.seaDetails.notifyParty?.email || "",
                phone: shipment.seaDetails.notifyParty?.phone || "",
              },
              containerType: shipment.seaDetails.containerType || "",
              sealNumber: shipment.seaDetails.sealNumber || "",
              masterBLNumber: shipment.seaDetails.masterBLNumber || "",
              blNumber: shipment.seaDetails.blNumber || "",
              blDate: shipment.seaDetails.blDate?.split("T")[0] || "",
              blType: shipment.seaDetails.blType || "",
              numberOfOriginals: shipment.seaDetails.numberOfOriginals || undefined,
              freightPaymentTerms: shipment.seaDetails.freightPaymentTerms || "",
              etd: shipment.seaDetails.etd?.split("T")[0] || "",
              eta: shipment.seaDetails.eta?.split("T")[0] || "",
              blRemarks: shipment.seaDetails.blRemarks || [],
              forwarder: {
                companyName: shipment.seaDetails.forwarder?.companyName || "",
                contactPerson:
                  shipment.seaDetails.forwarder?.contactPerson || "",
                email: shipment.seaDetails.forwarder?.email || "",
                phone: shipment.seaDetails.forwarder?.phone || "",
                houseBLNumber:
                  shipment.seaDetails.forwarder?.houseBLNumber || "",
                reference: shipment.seaDetails.forwarder?.reference || "",
              },
            }
          : undefined,
        airDetails: shipment.airDetails
          ? {
              airlineName: shipment.airDetails.airlineName || "",
              flightNumber: shipment.airDetails.flightNumber || "",
              airportOfDeparture:
                shipment.airDetails.airportOfDeparture || "",
              airportOfArrival: shipment.airDetails.airportOfArrival || "",
              mawbNumber: shipment.airDetails.mawbNumber || "",
              mawbDate: shipment.airDetails.mawbDate?.split("T")[0] || "",
              uldNumber: shipment.airDetails.uldNumber || "",
              etd: shipment.airDetails.etd?.split("T")[0] || "",
              eta: shipment.airDetails.eta?.split("T")[0] || "",
              forwarder: {
                companyName: shipment.airDetails.forwarder?.companyName || "",
                contactPerson:
                  shipment.airDetails.forwarder?.contactPerson || "",
                email: shipment.airDetails.forwarder?.email || "",
                phone: shipment.airDetails.forwarder?.phone || "",
                hawbNumber: shipment.airDetails.forwarder?.hawbNumber || "",
                reference: shipment.airDetails.forwarder?.reference || "",
              },
              awb: {
                awbNumber: shipment.airDetails.awb?.awbNumber || "",
                awbDate:
                  shipment.airDetails.awb?.awbDate?.split("T")[0] || "",
                issuingAgentName:
                  shipment.airDetails.awb?.issuingAgentName || "",
                issuingAgentCity:
                  shipment.airDetails.awb?.issuingAgentCity || "",
                freightPaymentTerms:
                  shipment.airDetails.awb?.freightPaymentTerms || "",
                otherChargesPayment:
                  shipment.airDetails.awb?.otherChargesPayment || "",
                declaredValueCarriage:
                  shipment.airDetails.awb?.declaredValueCarriage || "",
                declaredValueCustoms:
                  shipment.airDetails.awb?.declaredValueCustoms || "",
                currency: shipment.airDetails.awb?.currency || "",
                specialHandlingCodes:
                  shipment.airDetails.awb?.specialHandlingCodes || [],
                remarks: shipment.airDetails.awb?.remarks || [],
                notifyParty: {
                  companyName:
                    shipment.airDetails.awb?.notifyParty?.companyName || "",
                  contactPerson:
                    shipment.airDetails.awb?.notifyParty?.contactPerson || "",
                  email: shipment.airDetails.awb?.notifyParty?.email || "",
                  phone: shipment.airDetails.awb?.notifyParty?.phone || "",
                },
              },
            }
          : undefined,
        forwarder: {
          companyName: shipment.forwarder?.companyName || "",
          contactPerson: shipment.forwarder?.contactPerson || "",
          email: shipment.forwarder?.email || "",
          phone: shipment.forwarder?.phone || "",
          reference: shipment.forwarder?.reference || "",
        },
        carrier: {
          companyName: shipment?.carrier?.companyName,
          registerationCode: shipment?.carrier?.registerationCode || "",
          phoneNumber: shipment?.carrier?.phoneNumber || "",
          emailAddress: shipment?.carrier?.emailAddress || "",
          truckLicensePlate:
            typeof shipment?.carrier?.truckLicensePlate === "object"
              ? shipment?.carrier?.truckLicensePlate?._id
              : shipment?.carrier?.truckLicensePlate || null,
          trailerLicensePlate:
            typeof shipment?.carrier?.trailerLicensePlate === "object"
              ? shipment?.carrier?.trailerLicensePlate?._id
              : shipment?.carrier?.trailerLicensePlate || null,
          containerNumber: shipment?.carrier?.containerNumber || "",
          address: {
            ...shipment?.carrier?.address,
            country: getCountryId(shipment?.carrier?.address?.country || ""),
          },
        },
        assignedDriver:
          typeof shipment.assignedDriver === "object"
            ? shipment?.assignedDriver?._id
            : shipment.assignedDriver || null,
        cargoHandling: shipment.cargoHandling ?? {
          temperatureSensitive: false,
          adr: false,
          fragile: false,
        },
        cargoItems: shipment?.cargoItems || [],
        services: shipment.services || [],
        costServices: shipment.costServices || [],
        pricing: shipment.pricing
          ? {
              totalBuyingPrice: shipment.pricing.totalBuyingPrice ?? undefined,
              totalSellingPrice:
                shipment.pricing.totalSellingPrice ?? undefined,
              pricePerKm: shipment.pricing.pricePerKm ?? undefined,
              mileage: shipment.pricing.mileage ?? undefined,
              currency: shipment.pricing.currency || "EUR",
            }
          : undefined,
        additionalNotes: shipment.additionalNotes || "",
      });

      if (shipment.clientSync?.clientSameAsPickup) {
        setClientSyncMode("pickup");
      } else if (shipment.clientSync?.clientSameAsDelivery) {
        setClientSyncMode("delivery");
      } else {
        setClientSyncMode("none");
      }

      setNewAttachments([]);

      if (!isCopySource && shipment.waybill?.status === "submitted") {
        setIsWayBillCreated(true);
      } else {
        setIsWayBillCreated(false);
      }
    } else if (!isEditMode && !isCopySource) {
      reset({
        type: initialType || "International",
        status: "Order",
        pohLevel: 0,
        transportMode: initialTransportMode || "ROAD",
        shipmentDate: new Date().toISOString().split("T")[0],
        pickup: {
          date: "",
          time: "",
          companyName: "",
          registerationCode: "",
          phoneNumber: "",
          emailAddress: "",
          address: {
            street: "",
            city: "",
            state: "",
            postalCode: "",
            country: "",
          },
        },
        delivery: {
          date: "",
          time: "",
          companyName: "",
          registerationCode: "",
          phoneNumber: "",
          emailAddress: "",
          address: {
            street: "",
            city: "",
            state: "",
            postalCode: "",
            country: "",
          },
        },
        // Numeric cargo fields deliberately start blank (undefined), not 0 —
        // 0 is a real, distinct value the user can still enter explicitly.
        // react-hook-form renders an undefined default as an empty input.
        cargoItems: [
          {
            quantity: undefined,
            weightKg: undefined,
            ldm: undefined,
            width: undefined,
            length: undefined,
            height: undefined,
            description: "",
            marks: "",
            volumeM3: undefined,
          },
        ],
        services: [],
        costServices: [],
        cargoHandling: {
          temperatureSensitive: false,
          adr: false,
          fragile: false,
        },
        clientSync: {
          clientSameAsPickup: false,
          clientSameAsDelivery: false,
        },
        pricing: {
          currency: "EUR",
        },
        attachments: undefined,
        additionalNotes: "",
      });
      setNewAttachments([]);
      setIsWayBillCreated(false);
    }
  }, [
    shipment,
    isEditMode,
    isCopySource,
    reset,
    getCountryId,
    initialType,
    initialTransportMode,
  ]);

  useEffect(() => {
    setIsAssignedLocal(isAssigned);
  }, [isAssigned]);

  // Level 0 isn't available for SEA/AIR (it needs the road driver app). Those
  // modes use Level 1 or Level 2; coerce a stray/legacy 0 up to Level 1 while
  // preserving an explicitly chosen Level 2.
  const isSeaOrAir = transportMode === "SEA" || transportMode === "AIR";
  useEffect(() => {
    if (isSeaOrAir && getValues("pohLevel") === 0) {
      setValue("pohLevel", 1);
    }
  }, [isSeaOrAir, setValue, getValues]);

  // The status field is the manual-override path (for remote drivers / no-driver
  // SEA-AIR). It stays editable in edit mode for any non-completed, non-locked
  // shipment, regardless of POH level / type / mode. Forward-only is enforced by
  // the dropdown (single step) and the backend; gated steps prompt confirmation.
  const statusLifecycleLocked =
    shipment?.status === "Completed" || !!shipment?.driverAccess?.locked;

  // Transient: set true when the user confirms a status advance that bypasses a
  // POH signature / driver-file step; read once on save, then reset.
  const statusOverrideRef = useRef(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<null | {
    target: string;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
  }>(null);

  const statusValue = watch("status");

  // The status can only advance ONE step beyond what is actually saved, and
  // must be saved before it can advance again. Everything is anchored to the
  // persisted `shipment.status` — not the unsaved form value — so a user can't
  // walk the dropdown through several steps in one session and bypass the
  // per-step confirmations/blocks.
  const persistedStatus = shipment?.status ?? "Order";

  const commitStatus = (target: string, override: boolean) => {
    statusOverrideRef.current = override;
    setValue("status", target as CreateShipmentData["status"], {
      shouldDirty: true,
    });
  };

  const handleStatusSelect = (target: string) => {
    if (!target || target === statusValue) return;
    const decision = decideStatusChange(
      shipment?.pohLevel ?? 0,
      transportMode,
      persistedStatus,
      target,
      t,
    );
    if (decision.type === "block") {
      toast.error(decision.message);
      return; // controlled select snaps back to statusValue
    }
    if (decision.type === "silent") {
      commitStatus(target, false);
      return;
    }
    setPendingStatusChange({
      target,
      title: decision.title,
      message: decision.message,
      confirmText: decision.confirmText,
      cancelText: decision.cancelText,
    });
  };

  const handleAssignClick = async () => {
    try {
      if (!isAssignedLocal) {
        await onAssign();
        setIsAssignedLocal(true);
        toast.success(t("page.assignedToDriver"));
      } else {
        await onUnassign();
        setIsAssignedLocal(false);
        toast.success(t("page.unassignedFromDriver"));
      }
    } catch (err) {
      toast.error(typeof err === "string" && err ? err : t("form.failedToUpdateAssignment"));
    }
  };

  // Shared props for sub-components
  const sharedProps = {
    register,
    control,
    watch,
    setValue,
    errors,
    readonly,
    getErrorMessage,
    countryOptions,
    countries: countries?.flat() || [],
    apiKey: GOOGLE_PLACES_API_KEY,
  };

  const cargoProps = {
    fields,
    append,
    remove,
    totalQuantity,
    totalWeight,
    totalLDM,
    totalVolume,
  };

  const serviceProps = {
    serviceFields,
    appendService,
    removeService,
    totalForEachRow,
    grandTotal,
  };

  const costServiceProps = {
    costServiceFields,
    appendCostService,
    removeCostService,
    totalForEachRow: costTotalForEachRow,
    grandTotal: costGrandTotal,
  };

  // Post-completion service management: once a shipment is Completed (or the
  // driver journey locked it) the form can never be unlocked, but billable
  // extras still need to be added/removed from the system side. Services then
  // go through dedicated endpoints and the shipment is refetched.
  const postCompletionMode =
    isEditMode &&
    !!shipment &&
    (shipment.status === "Completed" || !!shipment.driverAccess?.locked);

  const handleApiAddService = useCallback(
    async (service: Record<string, unknown>) => {
      if (!shipment?._id) return;
      try {
        await shipmentAPI.addShipmentService(shipment._id, service);
        toast.success(t("services.addedSuccess"));
        await dispatch(getShipment(shipment._id));
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || t("services.addFailed"),
        );
        throw err;
      }
    },
    [shipment?._id, dispatch, t],
  );

  const handleApiDeleteService = useCallback(
    async (serviceId: string) => {
      if (!shipment?._id) return;
      try {
        await shipmentAPI.deleteShipmentService(shipment._id, serviceId);
        toast.success(t("services.deletedSuccess"));
        await dispatch(getShipment(shipment._id));
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || t("services.deleteFailed"),
        );
      }
    },
    [shipment?._id, dispatch, t],
  );

  const attachmentProps = {
    newAttachments,
    setNewAttachments,
    shipment,
    isEditMode,
    dispatch,
    downloadShipmentAttachment,
    toggleAttachmentPublic,
  };

  const waybillProps = {
    shipment,
    readonly,
    isWayBillCreated,
    setIsWayBillCreated,
    WayBillLoading,
    dispatch,
    createWayBill,
  };

  const formActionProps = {
    isEditMode,
    readonly,
    loading,
    shipment,
    isWayBillCreated,
    WayBillLoading,
    handleUnlockFields,
    handleLockFields,
    onFormSubmit: handleSubmit(onFormSubmit),
    onValidateCmr: handleValidateCmr,
    onValidateEfti: handleValidateEfti,
    shipmentType: type,
    transportMode,
    createWayBill,
    dispatch,
    setIsWayBillCreated,
    handleAssignClick,
    isAssignedLocal,
  };

  const emptyClient = {
    companyName: "",
    registerationCode: "",
    phoneNumber: "",
    emailAddress: "",
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
  };

  const mapToClient = (data?: PartyData): typeof emptyClient => ({
    companyName: data?.companyName ?? "",
    registerationCode: data?.registerationCode ?? "",
    phoneNumber: data?.phoneNumber ?? "",
    emailAddress: data?.emailAddress ?? "",
    address: {
      street: data?.address?.street ?? "",
      city: data?.address?.city ?? "",
      state: data?.address?.state ?? "",
      postalCode: data?.address?.postalCode ?? "",
      country: getCountryId(data?.address?.country ?? ""),
    },
  });

  useEffect(() => {
    const currentPickup =
      clientSyncMode === "pickup" ? pickupData : deliveryData;

    const merged = {
      ...currentPickup,
      ...mapToClient(client),
    };

    if (clientSyncMode === "pickup") {
      setValue("pickup", merged, { shouldValidate: true });
    } else if (clientSyncMode === "delivery") {
      setValue("delivery", merged, { shouldValidate: true });
    }
  }, [clientSyncMode, client, setValue]);

  // Forward-only, single step at a time, anchored to the SAVED status: only the
  // saved status and the immediate next one are selectable. This prevents
  // walking the dropdown several steps ahead in one unsaved session — each step
  // must be saved before the next becomes available. Every gated step (driver
  // signature/file, recipient signature) is handled by handleStatusSelect —
  // silently, via a confirmation, or blocked with a redirect to the handover.
  const savedStatusIdx = STATUS_STEPS.findIndex((s) => s.key === persistedStatus);
  const statusOptions = STATUS_STEPS.map((s, idx) => ({
    value: s.key,
    label: s.label,
    disabled: idx !== savedStatusIdx && idx !== savedStatusIdx + 1,
  }));

  /*
   * DEAD HANDLERS — kept commented for reference. Modal-based Add/Edit-partner
   * plumbing. All sections now bind their inline fields directly to RHF, so
   * none of this state-mirror is needed. Re-enable if/when a "Save as Partner"
   * modal flow is added on top of the inline UI.
   *
   * // Hydrate snapshots from the loaded shipment (edit mode):
   * useEffect(() => {
   *   if (shipment && isEditMode) {
   *     const newSnapshots: any = {};
   *     if (shipment?.pickup?.companyName) {
   *       newSnapshots.pickup = { ...shipment.pickup, isTemporary: false };
   *     }
   *     if (shipment?.delivery?.companyName) {
   *       newSnapshots.delivery = { ...shipment.delivery, isTemporary: false };
   *     }
   *     if (shipment?.carrier?.companyName) {
   *       newSnapshots.carrier = { ...shipment.carrier, isTemporary: false };
   *     }
   *     if (shipment?.client?.companyName) {
   *       newSnapshots.client = { ...shipment.client, isTemporary: false };
   *     }
   *     setSnapshots(newSnapshots);
   *   }
   * }, [shipment, isEditMode]);
   *
   * const handleSnapshotSelect = useCallback(
   *   (type: EntityType, snapshot: EntitySnapshot) => {
   *     setSnapshots((prev) => ({ ...prev, [type]: snapshot }));
   *   },
   *   [],
   * );
   *
   * const handleOpenModal = useCallback(
   *   (type: EntityType, mode: "add" | "edit") => {
   *     setModalState({
   *       isOpen: true,
   *       type,
   *       mode,
   *       snapshot: mode === "edit" ? snapshots[type] : undefined,
   *     });
   *   },
   *   [snapshots],
   * );
   *
   * const handleCloseModal = useCallback(() => {
   *   setModalState({ isOpen: false, type: null, mode: null, snapshot: undefined });
   * }, []);
   *
   * const handleSaveSnapshot = useCallback(
   *   (snapshot: EntitySnapshot) => {
   *     if (modalState.type) {
   *       setSnapshots((prev) => ({ ...prev, [modalState.type!]: snapshot }));
   *       const type = modalState.type;
   *       setValue(`${type}.companyName`, snapshot.companyName, { shouldValidate: true });
   *       setValue(`${type}.registerationCode`, snapshot.registerationCode || "", { shouldValidate: true });
   *       setValue(`${type}.phoneNumber`, snapshot.phoneNumber || "", { shouldValidate: true });
   *       setValue(`${type}.emailAddress`, snapshot.emailAddress || "", { shouldValidate: true });
   *       setValue(`${type}.address.street`, snapshot.address.street, { shouldValidate: true });
   *       setValue(`${type}.address.city`, snapshot.address.city, { shouldValidate: true });
   *       setValue(`${type}.address.state`, snapshot.address.state, { shouldValidate: true });
   *       setValue(`${type}.address.postalCode`, snapshot.address.postalCode || "", { shouldValidate: true });
   *       setValue(`${type}.address.country`, normalizeId(snapshot.address.country), { shouldValidate: true });
   *     }
   *   },
   *   [modalState.type, setValue],
   * );
   *
   * const handleClearSnapshot = useCallback((type: EntityType) => {
   *   setSnapshots((prev) => {
   *     const newSnapshots = { ...prev };
   *     delete newSnapshots[type];
   *     return newSnapshots;
   *   });
   * }, []);
   *
   * // Mirror selected client snapshot into pickup/delivery snapshot when "same as client" is on:
   * useEffect(() => {
   *   if (clientSyncMode === "pickup" && snapshots.client) {
   *     setSnapshots((prev) => ({ ...prev, pickup: snapshots.client }));
   *   } else if (clientSyncMode === "delivery" && snapshots.client) {
   *     setSnapshots((prev) => ({ ...prev, delivery: snapshots.client }));
   *   }
   * }, [clientSyncMode, snapshots.client]);
   *
   * // JSX mount near the bottom of the return:
   * // <EntityModal
   * //   isOpen={modalState.isOpen}
   * //   onClose={handleCloseModal}
   * //   type={modalState.type}
   * //   mode={modalState.mode}
   * //   snapshot={modalState.snapshot}
   * //   onSave={handleSaveSnapshot}
   * //   countries={countries?.flat() || []}
   * //   apiKey={GOOGLE_PLACES_API_KEY}
   * // />
   */

  const isGrouped = !!shipment?.groupId;

  useEffect(() => {
    const checkWidth = () => {
      setIsCmrCollapsed(window.innerWidth < 1800);
    };
    checkWidth(); // run on mount
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  return (
    <>
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        <div
          className={`w-full ${
            isCmrCollapsed ? "lg:w-[90%]" : "lg:w-[55%]"
          } overflow-y-auto lg:border-r border-gray-300 transition-all duration-300`}
        >
          {/* SO/PO source-order banner: shown while creating from an order
              (sourceOrder prop) and permanently on linked shipments
              (shipment.sourceOrderNumber). */}
          {(sourceOrder || shipment?.sourceOrderNumber) && (
            <div className="mx-4 mt-3 max-w-[1440px]">
              <div className="flex items-start justify-between gap-3 bg-sky-50 border border-sky-200 text-sky-900 text-[13px] rounded-md px-3 py-2">
                <div className="flex items-start gap-2 min-w-0">
                  <FileText className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium">
                      {sourceOrder
                        ? tOrders("banner.creatingFromOrder", {
                            number: sourceOrder.number,
                          })
                        : tOrders("banner.linkedToOrder", {
                            number: shipment?.sourceOrderNumber,
                          })}
                    </div>
                    {sourceOrder?.rawIncoterm && (
                      <div className="text-sky-800">
                        {tOrders("banner.rawIncoterm", {
                          code: sourceOrder.rawIncoterm,
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {(sourceOrder?.id || shipment?.sourceOrderId) && (
                  <a
                    href={`/orders/${sourceOrder?.id ?? shipment?.sourceOrderId}`}
                    className="shrink-0 text-sky-700 underline hover:text-sky-900"
                  >
                    {tOrders("banner.openOrder")}
                  </a>
                )}
              </div>
            </div>
          )}
          {isEditMode && readonly && shipment ? (
            <ShipmentReadonly shipment={shipment} />
          ) : (
            <form
              id="shipment-form"
              onSubmit={handleSubmit(onFormSubmit, () => {
                toast.error(t("form.missingInfoError"));
              })}
              className="space-y-6"
            >
              <div className="px-4 pt-2 pb-2 max-w-[1440px] mx-auto w-full">
                <div className="space-y-2">
                  <div className="bg-white rounded-md shadow-sm border border-gray-200 px-4 py-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <button
                          onClick={handleGoBack}
                          className="p-1.5 text-dark-600 hover:text-dark-900 hover:bg-gray-100 rounded-md transition-colors"
                          title={t("form.backToShipments")}
                        >
                          <ArrowLeft className="h-4 w-4" />
                        </button>
                        <h2 className="text-base font-bold text-dark-900 truncate">
                          {shipment?.shipmentNumber || shipmentNumber}
                        </h2>
                      </div>

                      <div className="flex items-center gap-3">
                        <FormField>
                          <div className="flex items-center gap-1.5">
                            <Select
                              {...transportModeField}
                              onChange={(e) => {
                                transportModeField.onChange(e);
                                handleTransportModeChange(e.target.value);
                              }}
                              options={transportModeOptions}
                              error={!!errors.transportMode}
                              disabled={isEditMode}
                              size="sm"
                              className={isEditMode ? "bg-gray-100" : ""}
                            />
                          </div>
                        </FormField>

                        <FormField>
                          <div className="flex items-center gap-1.5">
                            <Select
                              {...register("pohLevel", { valueAsNumber: true })}
                              options={
                                isSeaOrAir
                                  ? [
                                      { value: "1", label: "POH Level 1" },
                                      { value: "2", label: "POH Level 2" },
                                    ]
                                  : [
                                      { value: "0", label: "POH Level 0" },
                                      { value: "1", label: "POH Level 1" },
                                      { value: "2", label: "POH Level 2" },
                                    ]
                              }
                              size="sm"
                            />
                          </div>
                        </FormField>

                        {isEditMode && shipment ? (
                          <FormField error={getErrorMessage(errors.status)}>
                            <Select
                              name="status"
                              value={statusValue ?? ""}
                              onChange={(e) => handleStatusSelect(e.target.value)}
                              error={!!errors.status}
                              options={statusOptions}
                              disabled={statusLifecycleLocked}
                              size="sm"
                              className={statusLifecycleLocked ? "bg-gray-100" : ""}
                            />
                          </FormField>
                        ) : (
                          <FormField error={getErrorMessage(errors.status)}>
                            <Input
                              {...register("status")}
                              defaultValue={"Order"}
                              readOnly={true}
                              error={!!errors.status}
                              size="sm"
                              className="bg-gray-100"
                            />
                          </FormField>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    <div className="flex flex-col justify-between space-y-2 ">
                      <GeneralInformation
                        {...sharedProps}
                        shipment={shipment}
                        isEditMode={isEditMode}
                        isAssignedToDriverApp={isAssignedLocal}
                      />
                      <CarrierClientSection
                        {...sharedProps}
                        clientSyncMode={clientSyncMode}
                      />
                    </div>
                    <TransportSection
                      {...sharedProps}
                      users={users}
                      trucks={trucks}
                      trailers={trailers}
                      watch={watch}
                      isEditMode={isEditMode}
                      isDriverLocked={shipment?.driverAccess?.locked}
                      loading={loading}
                      isAssignedLocal={isAssignedLocal}
                      handleAssignClick={handleAssignClick}
                      isGrouped={isGrouped}
                      persistedDriverId={
                        shipment
                          ? typeof shipment.assignedDriver === "object"
                            ? shipment.assignedDriver?._id ?? null
                            : shipment.assignedDriver ?? null
                          : null
                      }
                    />
                  </div>

                  {transportMode === "SEA" && (
                    <>
                      <SeaDetailsSection {...sharedProps} />
                      <BillOfLadingSection {...sharedProps} />
                    </>
                  )}

                  {transportMode === "AIR" && (
                    <>
                      <AirDetailsSection {...sharedProps} />
                      <AwbSection
                        register={register}
                        control={control}
                        errors={errors}
                        readonly={readonly}
                        getErrorMessage={getErrorMessage}
                      />
                    </>
                  )}

                  {/* Forwarder — SEA/AIR carry it inside their details cards;
                      ROAD gets the standalone top-level forwarder block here. */}
                  {transportMode === "ROAD" && (
                    <ForwarderSection {...sharedProps} />
                  )}

                  <PickupDeliverySection
                    {...sharedProps}
                    clientSyncMode={clientSyncMode}
                    setClientSyncMode={setClientSyncMode}
                  />

                  <CargoItemsSection
                    {...sharedProps}
                    {...cargoProps}
                    transportMode={transportMode}
                  />

                  {/* B/L Remarks — SEA only, after the Cargo section */}
                  {transportMode === "SEA" && (
                    <BLRemarksSection
                      register={register}
                      errors={errors}
                      readonly={readonly}
                      control={control}
                    />
                  )}

                  <PricingSection
                    {...sharedProps}
                    currencies={CURRENCIES}
                    transportMode={transportMode}
                  />

                  <ServicesSection
                    {...sharedProps}
                    {...serviceProps}
                    services={services}
                    postCompletionMode={postCompletionMode}
                    onApiAddService={handleApiAddService}
                    onApiDeleteService={handleApiDeleteService}
                  />

                  <CostServicesSection
                    {...sharedProps}
                    {...costServiceProps}
                    services={costServices}
                  />

                  <AttachmentsSection {...sharedProps} {...attachmentProps} />
                </div>
              </div>
            </form>
          )}

          <div className="space-y-6">
            <div className="px-4 max-w-[1440px] mx-auto w-full">
              <div className="space-y-2">
                {/* Waybill (CMR) is road-specific — hidden for SEA / AIR */}
                {transportMode === "ROAD" && (
                  <WaybillSection {...waybillProps} />
                )}

                {isEditMode && !readonly && shipment && (
                  <CommentSection
                    shipmentId={shipment?._id}
                    title={t("comments.titleToDriver")}
                  />
                )}

                {isEditMode && !readonly && shipment && (
                  <div className="bg-white rounded-md shadow-sm border border-gray-200">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
                      <span className="text-[13px] font-semibold text-gray-700 uppercase tracking-wide">Reclamations</span>
                    </div>
                    <div className="px-4 py-3">
                      <ReclamationSection shipmentId={shipment._id} />
                    </div>
                  </div>
                )}

                {/* Invoices billed against this shipment (edit mode). Read-only
                    mode renders this inside ShipmentReadonly's content column. */}
                {isEditMode && !readonly && shipment && (
                  <div className="pb-6">
                    <InvoiceRefs shipmentId={shipment._id} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={`hidden lg:block ${
            isCmrCollapsed ? "lg:w-[10%]" : "lg:w-[45%]"
          } transition-all duration-300 relative bg-white`}
        >
          {/* Toggle Button */}
          <button
            onClick={() => setIsCmrCollapsed(!isCmrCollapsed)}
            className="absolute top-1/2 -translate-y-1/2 -left-3 z-10 bg-white border border-gray-300 rounded-full p-1.5 shadow-md hover:bg-gray-50 transition-colors"
            title={
              isCmrCollapsed
                ? t("form.expandPreview", { type: previewDocLabel })
                : t("form.collapsePreview", { type: previewDocLabel })
            }
          >
            {isCmrCollapsed ? (
              <ChevronLeft className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          <div
            className={`h-full overflow-y-auto py-4 px-2 ${
              isCmrCollapsed ? "overflow-hidden" : ""
            }`}
          >
            <div className="h-full">
              {isCmrCollapsed ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-500">
                  <FileText className="h-8 w-8 mb-2" />
                  <div className="text-xs font-medium text-center">
                    {previewDocLabel}
                    <br />
                    {t("form.preview")}
                  </div>
                </div>
              ) : (
                <>
                  <div className="border border-gray-300 bg-white min-h-[600px] p-3 text-xs font-mono scale-90 origin-top">
                    {transportMode === "SEA" ? (
                      <BolPreview
                        shipment={order}
                        seaDetails={seaDetailsData as SeaDetails | undefined}
                        countries={countries}
                      />
                    ) : transportMode === "AIR" ? (
                      <AwbPreview
                        shipment={order}
                        airDetails={airDetailsData as AirDetails | undefined}
                        countries={countries}
                      />
                    ) : order && order.type === "International" ? (
                      <CmrPreview
                        shipment={order}
                        countries={countries}
                        trucks={trucks}
                        trailers={trailers}
                      />
                    ) : (
                      <DeliveryNotePreview
                        data={order}
                        countries={countries}
                        trucks={trucks}
                        trailers={trailers}
                        users={users}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <FormActions {...formActionProps} />

      {pendingStatusChange && (
        <ConfirmationDialog
          isOpen={true}
          onClose={() => setPendingStatusChange(null)}
          onConfirm={() => commitStatus(pendingStatusChange.target, true)}
          title={pendingStatusChange.title}
          message={pendingStatusChange.message}
          confirmText={pendingStatusChange.confirmText}
          cancelText={pendingStatusChange.cancelText}
          type="warning"
        />
      )}
    </>
  );
};
